/**
 * abaco-attribution.js
 *
 * First-party UTM and click attribution persistence.
 *
 * Capture behavior (runs on every page load):
 *   - Reads UTM params and msclkid from the current URL
 *   - If at least one attribution param is present, writes to localStorage
 *     under the key 'abaco_attribution', overwriting any prior value
 *   - If no attribution params are present, does nothing — existing stored
 *     attribution is preserved
 *
 * Read behavior:
 *   - Exposes window.getAttribution() which safely reads and parses stored
 *     attribution, returning a consistent object with empty strings for any
 *     missing fields
 *   - Never throws — all localStorage access is wrapped in try/catch to
 *     handle private browsing and storage quota errors gracefully
 */

(function () {

  var STORAGE_KEY = 'abaco_attribution';
  var PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'msclkid', 'fbclid'];
  var MAX_VALUE_LENGTH = 500;

  /**
   * Safely cast a value to a trimmed string and truncate to MAX_VALUE_LENGTH.
   * Returns empty string for anything null, undefined, or non-string-castable.
   */
  function sanitize(value) {
    if (value === null || value === undefined) return '';
    var str = String(value).trim();
    return str.length > MAX_VALUE_LENGTH ? str.slice(0, MAX_VALUE_LENGTH) : str;
  }

  /**
   * Read and parse the stored attribution object from localStorage.
   * Returns null if nothing is stored or if parsing fails.
   */
  function readStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /**
   * Write an attribution object to localStorage.
   * Silently fails if localStorage is unavailable (e.g. private browsing).
   */
  function writeStored(obj) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      // Silently degrade — form submission must not be affected
    }
  }

  /**
   * Capture attribution from the current URL if any params are present.
   * Called once on every page load.
   */
  function capture() {
    try {
      var params = new URLSearchParams(window.location.search);

      // Check if at least one attribution param exists in the current URL
      var hasAttribution = PARAM_KEYS.some(function (key) {
        return params.get(key);
      });

      // If no attribution params are present, preserve existing stored data
      if (!hasAttribution) return;

      var attribution = {
        utm_source:   sanitize(params.get('utm_source')),
        utm_medium:   sanitize(params.get('utm_medium')),
        utm_campaign: sanitize(params.get('utm_campaign')),
        utm_term:     sanitize(params.get('utm_term')),
        utm_content:  sanitize(params.get('utm_content')),
        msclkid:      sanitize(params.get('msclkid')),
        fbclid:      sanitize(params.get('fbclid')),
        landing_page: sanitize(window.location.href),
        timestamp:    new Date().toISOString()
      };

      writeStored(attribution);

    } catch (e) {
      // Silently degrade — never interrupt page load
    }
  }

  /**
   * Public read helper.
   * Returns a consistent attribution object with empty strings for any
   * missing fields. Never throws.
   *
   * Usage: var attribution = window.getAttribution();
   */
  function getAttribution() {
    var defaults = {
      utm_source:   '',
      utm_medium:   '',
      utm_campaign: '',
      utm_term:     '',
      utm_content:  '',
      msclkid:      '',
      fbclid:      '',
      landing_page: '',
      timestamp:    ''
    };

    try {
      var stored = readStored();
      if (!stored || typeof stored !== 'object') return defaults;

      return {
        utm_source:   sanitize(stored.utm_source),
        utm_medium:   sanitize(stored.utm_medium),
        utm_campaign: sanitize(stored.utm_campaign),
        utm_term:     sanitize(stored.utm_term),
        utm_content:  sanitize(stored.utm_content),
        msclkid:      sanitize(stored.msclkid),
        fbclid:      sanitize(stored.fbclid),
        landing_page: sanitize(stored.landing_page),
        timestamp:    sanitize(stored.timestamp)
      };
    } catch (e) {
      return defaults;
    }
  }

  // Run capture on every page load
  capture();

  // Expose read helper globally for use in form submit handlers
  window.getAttribution = getAttribution;

})();
